import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/auth-store';

const EULA_STATIC_PATH = '/legal/eula/index.html';
const EULA_VERSION = '2026-03-07';
const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
const ACCEPT_EULA_ENDPOINT = `${API_BASE_URL ? `${API_BASE_URL}/trpc` : '/trpc'}/auth.acceptEula`;

type AcceptEulaResponse = {
  acceptedAt?: string;
  alreadyAccepted?: boolean;
  version?: string;
};

async function acceptEula(version: string, accessToken: string): Promise<AcceptEulaResponse> {
  const response = await fetch(ACCEPT_EULA_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      json: { version },
    }),
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const envelope = Array.isArray(payload) ? payload[0] : payload;
  const trpcError = (envelope as { error?: { message?: string } } | null)?.error;

  if (!response.ok || trpcError) {
    throw new Error(trpcError?.message ?? 'Unable to record EULA acceptance.');
  }

  return ((envelope as { result?: { data?: { json?: AcceptEulaResponse } } } | null)?.result?.data?.json ?? {});
}

export function EulaPage() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [eulaHtml, setEulaHtml] = useState<string>('');
  const [loadError, setLoadError] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetch(EULA_STATIC_PATH, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load EULA (${response.status})`);
        }
        return response.text();
      })
      .then((html) => {
        if (isMounted) {
          setEulaHtml(html);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLoadError(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!eulaHtml) {
      return;
    }

    const checkbox = document.getElementById('calculom-eula-check') as HTMLInputElement | null;
    const acceptButton = document.getElementById('calculom-eula-accept') as HTMLButtonElement | null;
    const declineButton = document.querySelector<HTMLButtonElement>('.eula-btn-decline');

    if (!checkbox || !acceptButton) {
      return;
    }

    checkbox.removeAttribute('onchange');
    acceptButton.removeAttribute('onclick');
    declineButton?.removeAttribute('onclick');

    const syncButtonVisualState = () => {
      acceptButton.className = checkbox.checked ? 'eula-btn-agree active' : 'eula-btn-agree';
    };

    syncButtonVisualState();

    const onCheckboxChange = () => {
      syncButtonVisualState();
    };

    const onAcceptClick = async (event: Event) => {
      event.preventDefault();

      if (!checkbox.checked) {
        setStatusMessage('Please check the agreement box before continuing.');
        return;
      }

      if (!isAuthenticated || !accessToken) {
        setStatusMessage('Sign in to your Calculom account before accepting the EULA.');
        return;
      }

      setStatusMessage('');
      setIsSubmitting(true);
      acceptButton.disabled = true;

      try {
        const result = await acceptEula(EULA_VERSION, accessToken);
        const acceptedAt = result.acceptedAt ? new Date(result.acceptedAt) : null;
        const acceptedAtText = acceptedAt && !Number.isNaN(acceptedAt.getTime())
          ? acceptedAt.toLocaleString()
          : 'now';

        setStatusMessage(
          result.alreadyAccepted
            ? `EULA version ${result.version ?? EULA_VERSION} was already accepted on ${acceptedAtText}.`
            : `EULA version ${result.version ?? EULA_VERSION} accepted on ${acceptedAtText}.`
        );

        acceptButton.textContent = result.alreadyAccepted ? 'Already Accepted' : 'Accepted';
        acceptButton.className = 'eula-btn-agree active';
        acceptButton.disabled = true;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to record EULA acceptance.';
        setStatusMessage(message);
        acceptButton.disabled = false;
        syncButtonVisualState();
      } finally {
        setIsSubmitting(false);
      }
    };

    const onDeclineClick = (event: Event) => {
      event.preventDefault();
      window.history.back();
    };

    checkbox.addEventListener('change', onCheckboxChange);
    acceptButton.addEventListener('click', onAcceptClick);
    declineButton?.addEventListener('click', onDeclineClick);

    return () => {
      checkbox.removeEventListener('change', onCheckboxChange);
      acceptButton.removeEventListener('click', onAcceptClick);
      declineButton?.removeEventListener('click', onDeclineClick);
    };
  }, [eulaHtml, accessToken, isAuthenticated]);

  if (loadError) {
    return (
      <main className="min-h-screen bg-white p-8 text-slate-800">
        <h1 className="text-2xl font-semibold">End-User License Agreement</h1>
        <p className="mt-3">
          The EULA could not be loaded in this view. You can open it directly at{' '}
          <a className="text-blue-600 underline" href={EULA_STATIC_PATH}>
            {EULA_STATIC_PATH}
          </a>
          .
        </p>
      </main>
    );
  }

  if (!eulaHtml) {
    return (
      <main className="min-h-screen bg-white p-8 text-slate-800">
        <p>Loading End-User License Agreement...</p>
      </main>
    );
  }

  return (
    <>
      {(statusMessage || isSubmitting) && (
        <div
          className={`mx-auto mt-4 max-w-4xl rounded border px-4 py-3 text-sm ${
            statusMessage.toLowerCase().includes('accepted')
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
          role="status"
          aria-live="polite"
        >
          {isSubmitting ? 'Recording your acceptance...' : statusMessage}
        </div>
      )}
      <main className="min-h-screen bg-white" dangerouslySetInnerHTML={{ __html: eulaHtml }} />
    </>
  );
}
